import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  ApiRequestStatus,
  FileDownloaded,
  FileDownloadedType,
} from '../../../model';
import { DownloaderMediaType } from '../../../model/downloader-media-type';
import { GlobalActions } from '../../../model/global-actions';
import {
  downloadWalletFile,
  getCurrentState,
  getSelectedWalletSection,
  messagesForWalletError,
  myWalletSliceActions,
  sharedAccountsSliceActions,
} from '../../../store/features/wallet';
import { useAppDispatch, useAppSelector } from '../../redux';

export interface MyWalletDownloadFileLogic {
  fileDownloaded: FileDownloaded;
  fileDownloadedRequestStatus: ApiRequestStatus;
  onDownloadFile: (
    type: FileDownloadedType,
    mediaType: DownloaderMediaType
  ) => void;
  resetDownloadStatus: () => void;
}

export const useMyWalletDownloadFileLogic = (
  globalActions: GlobalActions
): MyWalletDownloadFileLogic => {
  const { formatMessage } = useIntl();
  const { showToast, closeToast } = globalActions;
  const dispatch = useAppDispatch();

  const { fileDownloaded, fileDownloadedRequestStatus, fileDownloadedError }:any =
    useAppSelector(getCurrentState);

  const selectedWalletSection = useAppSelector(getSelectedWalletSection);

  const actions = useMemo(
    () =>
      selectedWalletSection === 'MY_WALLET'
        ? myWalletSliceActions
        : sharedAccountsSliceActions,
    [selectedWalletSection]
  );

  const onDownloadFile = useCallback(
    (type: FileDownloadedType, mediaType: DownloaderMediaType) => {
      dispatch(downloadWalletFile({ type, mediaType }));
    },
    [dispatch]
  );

  const resetDownloadStatus = useCallback(() => {
    dispatch(actions.clearFileDownloadedError());
  }, [dispatch, actions]);

  useEffect(() => {
    if (fileDownloadedError) {
      const errorMessages = messagesForWalletError(fileDownloadedError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(actions.clearFileDownloadedError());
    }
  }, [
    dispatch,
    fileDownloadedError,
    closeToast,
    formatMessage,
    showToast,
    actions,
  ]);

  return {
    fileDownloaded,
    fileDownloadedRequestStatus,

    onDownloadFile,
    resetDownloadStatus,
  };
};
